import React, { useRef } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

import {
  FaMapMarkerAlt,
  FaPaperPlane,
  FaGlobeAmericas,
  FaChevronRight,
  FaCheckCircle,
  FaStar,
  FaChevronDown,
  FaTimesCircle,
  FaBus,
  FaHotel,
  FaUtensils,
  FaShieldAlt,
  FaMountain,
  FaTree,
  FaRoad,
} from "react-icons/fa";

import "./DibangValley.css";

const DibangValley = () => {
  const itineraryRef = useRef(null);
  const highlightsRef = useRef(null);
  const inclusionsRef = useRef(null);
  const aboutRef = useRef(null);

  const scrollToSection = (ref) => {
    window.scrollTo({
      top: ref.current.offsetTop - 100,
      behavior: "smooth",
    });
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.7,
        ease: "easeOut",
      },
    },
  };

  const itineraryData = [
    {
      day: 1,
      title: "Dibrugarh to Roing | Scenic Entry into Arunachal",
      points: [
        "Begin your journey from Dibrugarh towards Arunachal Pradesh.",
        "Drive through lush Assamese villages and scenic countryside.",
        "Cross the iconic Dhola–Sadiya Bridge over the Brahmaputra River.",
        "Arrive in Roing and check in to your accommodation.",
        "Enjoy a peaceful evening walk around the town.",
        "Overnight stay in Roing.",
      ],
      icon: <FaRoad />,
    },

    {
      day: 2,
      title: "Roing to Anini via Mayodia Pass",
      points: [
        "After breakfast depart towards Anini in Dibang Valley.",
        "Drive through the breathtaking Mayodia Pass (2,655 meters).",
        "Enjoy scenic mountain roads, forests, and misty valleys.",
        "Pass through Hunli and Etalin villages.",
        "Arrive in Anini and check in to your homestay.",
        "Overnight stay in Anini.",
      ],
      icon: <FaMountain />,
    },

    {
      day: 3,
      title: "Excursion to Acheso & Chigu Camp",
      points: [
        "Explore the untouched beauty of Dibang Valley.",
        "Proceed towards Acheso with dramatic mountain landscapes.",
        "Visit the scenic Chigu Camp surrounded by valleys and peaks.",
        "Enjoy photography opportunities and peaceful nature walks.",
        "Spend time amidst remote Himalayan scenery.",
        "Return to Anini by evening.",
      ],
      icon: <FaTree />,
    },

    {
      day: 4,
      title: "Anini Local Sightseeing",
      points: [
        "Visit Anini Viewpoint for panoramic mountain views.",
        "Explore the beautiful Mathun Valley with green meadows.",
        "Experience peaceful Himalayan landscapes and fresh mountain air.",
        "Visit local markets and interact with local communities.",
        "Enjoy the calm atmosphere of Anini town.",
        "Overnight stay in Anini.",
      ],
      icon: <FaMountain />,
    },

    {
      day: 5,
      title: "Anini to Roing | Return Journey",
      points: [
        "After breakfast begin your return journey towards Roing.",
        "Drive once again through scenic Dibang Valley routes.",
        "Enjoy views of forests, valleys, and winding mountain roads.",
        "Arrive in Roing and check in to your accommodation.",
        "Relax and enjoy a peaceful evening.",
        "Overnight stay in Roing.",
      ],
      icon: <FaRoad />,
    },

    {
      day: 6,
      title: "Roing to Dibrugarh via Namsai",
      points: [
        "Depart from Roing towards Dibrugarh.",
        "Visit the famous Golden Pagoda (Kongmu Kham) in Namsai.",
        "Explore the peaceful Buddhist temple surroundings.",
        "Continue your scenic drive towards Dibrugarh.",
        "Tour concludes with memorable Arunachal experiences.",
      ],
      icon: <FaCheckCircle />,
    },
  ];

  return (
    <div className="trip-container">

      {/* HEADER */}
      <motion.header
        initial="hidden"
        whileInView="visible"
        viewport={{ once: false }}
        variants={fadeInUp}
        className="trip-header"
      >
        <div className="header-left">
          <h1 className="trip-title">
            Hidden Arunachal: Dibang Valley & Anini Expedition
          </h1>

          <p className="trip-duration">
            5 Nights / 6 Days
          </p>
        </div>

        <div className="header-right">

          <div className="info-row">
            <FaMapMarkerAlt className="header-icon" />

            <div className="info-text">
              <span>Destinations:</span>
              <p>
                Dibrugarh • Roing • Mayodia Pass • Anini • Dibang Valley
              </p>
            </div>
          </div>

          <div className="info-row">
            <FaPaperPlane className="header-icon" />

            <div className="info-text">
              <span>Start & End:</span>
              <p>Dibrugarh</p>
            </div>
          </div>

          <div className="info-row type-tag">
            <FaGlobeAmericas className="header-icon" />
            <p>Mountains • Nature • Offbeat • Adventure</p>
          </div>

        </div>
      </motion.header>

      {/* IMAGE GRID */}
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: false }}
        variants={fadeInUp}
        className="image-grid-container"
      >

        <div className="grid-item large-vertical">
          <img
            src="https://images.pexels.com/photos/10059881/pexels-photo-10059881.jpeg"
            alt="Dibang Valley"
          />
        </div>

        <div className="grid-item middle-box">
          <img
            src="https://images.pexels.com/photos/34644778/pexels-photo-34644778.jpeg"
            alt="Mayodia Pass"
          />
        </div>

        <div className="right-stack">

          <div className="grid-item small-h">
            <img
              src="https://images.pexels.com/photos/8747702/pexels-photo-8747702.jpeg"
              alt="Mountain Road"
            />
          </div>

          <div className="grid-item small-h">
            <img
              src="https://images.pexels.com/photos/33512846/pexels-photo-33512846.jpeg"
              alt="Forest Valley"
            />
          </div>

        </div>
      </motion.div>

      {/* STICKY NAV */}
      <nav className="sticky-nav">

        <button
          className="tab-link"
          onClick={() => scrollToSection(itineraryRef)}
        >
          Detailed Itinerary
        </button>

        <button
          className="tab-link"
          onClick={() => scrollToSection(highlightsRef)}
        >
          Highlights
        </button>

        <button
          className="tab-link"
          onClick={() => scrollToSection(inclusionsRef)}
        >
          Policy & Inclusions
        </button>

        <button
          className="tab-link"
          onClick={() => scrollToSection(aboutRef)}
        >
          About Trip
        </button>

      </nav>

      <div className="content-section-wrapper">

        {/* ITINERARY */}
        <section ref={itineraryRef} className="scroll-section">

          <h2 className="section-main-title">
            Tour Itinerary
          </h2>

          <div className="days-list">

            {itineraryData.map((item) => (
              <motion.div
                key={item.day}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: false, amount: 0.2 }}
                variants={fadeInUp}
                className="day-block"
              >

                <div className="day-header">

                  <span className="day-badge">
                    Day {item.day}
                  </span>

                  <h3>{item.title}</h3>

                </div>

                <div className="day-body">

                  {item.points.map((p, i) => (
                    <div key={i} className="point-item">
                      <FaChevronRight className="bullet-icon" />
                      <p>{p}</p>
                    </div>
                  ))}

                </div>

              </motion.div>
            ))}

          </div>

        </section>

        {/* HIGHLIGHTS */}
        <motion.section
          ref={highlightsRef}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false }}
          variants={fadeInUp}
          className="scroll-section"
        >

          <h2 className="section-main-title">
            Trip Highlights
          </h2>

          <div className="highlights-grid">

            <div className="h-item">
              <FaStar className="star-icon" />
              <p>
                Scenic road journey through Assam and Arunachal Pradesh
              </p>
            </div>

            <div className="h-item">
              <FaStar className="star-icon" />
              <p>
                Cross the iconic Dhola–Sadiya Bridge
              </p>
            </div>

            <div className="h-item">
              <FaStar className="star-icon" />
              <p>
                Drive through breathtaking Mayodia Pass
              </p>
            </div>

            <div className="h-item">
              <FaStar className="star-icon" />
              <p>
                Stay in the remote Himalayan town of Anini
              </p>
            </div>

            <div className="h-item">
              <FaStar className="star-icon" />
              <p>
                Explore untouched landscapes of Dibang Valley
              </p>
            </div>

            <div className="h-item">
              <FaStar className="star-icon" />
              <p>
                Visit Chigu Camp and Acheso Valley
              </p>
            </div>

            <div className="h-item">
              <FaStar className="star-icon" />
              <p>
                Enjoy panoramic views from Anini Viewpoint
              </p>
            </div>

            <div className="h-item">
              <FaStar className="star-icon" />
              <p>
                Visit the peaceful Golden Pagoda in Namsai
              </p>
            </div>

          </div>

        </motion.section>

        {/* INCLUSIONS */}
        <motion.section
          ref={inclusionsRef}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false }}
          variants={fadeInUp}
          className="scroll-section"
        >

          <div className="policy-box inclusion-box">

            <h4 className="p-title green">
              <FaCheckCircle />
              What's Included
            </h4>

            <ul className="p-list">

              <li>
                <FaBus />
                Transportation from Dibrugarh to Dibrugarh
              </li>

              <li>
                <FaCheckCircle />
                Inner Line Permit (ILP) for Arunachal Pradesh
              </li>

              <li>
                <FaCheckCircle />
                All toll charges during the journey
              </li>

              <li>
                <FaHotel />
                Accommodation for 5 nights
              </li>

              <li>
                <FaUtensils />
                Breakfast and dinner
              </li>

              <li>
                <FaShieldAlt />
                Experienced local driver and coordinator support
              </li>

            </ul>

          </div>

          <div className="policy-box exclusion-box">

            <h4 className="p-title red">
              <FaTimesCircle />
              What's Excluded
            </h4>

            <ul className="p-list">

              <li>
                <FaTimesCircle />
                Airfare or train tickets to Dibrugarh
              </li>

              <li>
                <FaUtensils />
                Lunch and snacks
              </li>

              <li>
                <FaTimesCircle />
                Personal expenses and shopping
              </li>

              <li>
                <FaTimesCircle />
                Monument or sightseeing entry fees
              </li>

              <li>
                <FaTimesCircle />
                PAP charges for foreign nationals
              </li>

              <li>
                <FaTimesCircle />
                Parking and incidental charges
              </li>

              <li>
                <FaTimesCircle />
                Room heater charges
              </li>

              <li>
                <FaTimesCircle />
                Extra expenses due to weather or roadblocks
              </li>

              <li>
                <FaShieldAlt />
                Any service not mentioned in inclusions
              </li>

            </ul>

          </div>

        </motion.section>

        {/* ABOUT */}
        <motion.section
          ref={aboutRef}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false }}
          variants={fadeInUp}
          className="scroll-section"
        >

          <h2 className="section-main-title">
            About This Journey
          </h2>

          <p className="about-text">
            The Anini Valley Expedition is a journey into one of the most remote
            and untouched regions of Northeast India. This tour takes you deep
            into the scenic landscapes of Arunachal Pradesh, covering
            Dibrugarh, Roing, the breathtaking Mayodia Pass, the pristine
            Dibang Valley, and the serene town of Anini.
            <br /><br />
            Surrounded by dense forests, high mountain passes, vast valleys,
            and traditional Himalayan villages, this trip offers a rare
            opportunity to experience raw natural beauty and authentic local
            culture.
          </p>

        </motion.section>

        {/* TERMS */}
        <div className="accordion-item">

          <Link
            to="/terms-conditions"
            className="accordion-header"
            style={{
              textDecoration: "none",
              color: "inherit",
            }}
          >

            <span>Terms & Conditions</span>

            <FaChevronDown
              className="arrow-icon"
              style={{ transform: "rotate(-90deg)" }}
            />

          </Link>

        </div>

        {/* PRIVACY */}
        <div className="accordion-item">

          <Link
            to="/privacy-policy"
            className="accordion-header"
            style={{
              textDecoration: "none",
              color: "inherit",
            }}
          >

            <span>Privacy Policy</span>

            <FaChevronDown
              className="arrow-icon"
              style={{ transform: "rotate(-90deg)" }}
            />

          </Link>

        </div>

        {/* IMPORTANT NOTES */}
        <motion.section
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false }}
          variants={fadeInUp}
          className="scroll-section travel-info-grid"
        >

          <div
            className="info-card note-card"
            style={{ width: "100%" }}
          >

            <h4 className="info-card-title">
              <FaPaperPlane />
              Important Notes
            </h4>

            <p>
              • This expedition involves long mountain drives and remote travel.
              <br /><br />
              • Weather conditions in Arunachal Pradesh may affect road conditions.
              <br /><br />
              • Mobile connectivity may be limited in remote areas like Anini.
              <br /><br />
              • Carry warm clothing and essential medicines for the journey.
            </p>

          </div>

        </motion.section>

      </div>
    </div>
  );
};

export default DibangValley;